/**
 * JobOfferReviewWorkspace — canonical /installer-offers/:jobId/review surface.
 *
 * Primary dispatcher workflow for reviewing scope + setting Offer
 * Compensation before sending an installer offer. Pure composition over
 * canonical selectors — no new business logic, no schema, no writers
 * beyond the existing `setJobPayoutAmount` and `createOffer` paths.
 *
 * Sections (MVP):
 *   1. Header strip           — customer / job / stage / install date / city
 *   2. Scope & Notes          — scopeSummary + notes + project type
 *   3. Photos                 — job.photos[] gallery
 *   4. BOM                    — DB-backed job.materialsList
 *   5. Compensation Builder   — OfferCompensationEditor + sale-aware %
 *                               (finance-permission gated) + permit/risk
 *                               warnings + eligible installer count
 *   6. Comparables            — getComparableJobOffers()
 *
 * Footer: Send Offer (opens OfferContractorDialog) · View Job · Back.
 *
 * NEVER exposes Job.financials.contractValue to roles without
 * useFinancialPermissions().viewContractValue. Installer-facing
 * surfaces remain blind to all economics.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { useJobDetailQuery } from "@/hooks/useJobDetailQuery";
import { useRole } from "@/data/RoleContext";
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

import { useMarketplaceOffers } from "@/hooks/useMarketplaceOffers";
import { useReactQueryContractors } from "@/hooks/useReactQueryContractors";
import { useReactQueryPayProfiles } from "@/hooks/useReactQueryPayProfiles";
import { getJobBriefing } from "@/data/contractor/jobBriefing";
import { getJobSitePhotoMeta } from "@/services/attachment.service";

import { resolveContractorEligibility } from "@/data/contractor/marketplaceEligibility";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import {
  getContractorAvailability,
  bucketFor,
  describeAvailability,
  AVAILABILITY_BUCKET_LABEL,
  type AvailabilityBucket,
} from "@/data/contractor/availability";
import { resolvePermitOperationalSignals } from "@/data/permitting/permitOperationalSignals";
import { getComparableJobOffers } from "@/data/contractor/comparableJobOffers";
import { CompensationBuilder } from "@/components/installerOffers/CompensationBuilder";
import type { CompensationDraftPreview } from "@/components/installerOffers/CompensationBuilder";
import { ProjectedJobEconomicsPanel } from "@/components/installerOffers/ProjectedJobEconomicsPanel";
import { OfferContractorDialog } from "@/components/contractor/OfferContractorDialog";
import { InstallerInvoiceDialog } from "@/components/contractor/InstallerInvoiceDialog";
import { InstallerPayoutsService, type ReadyToPayItem } from "@/services/installerPayouts.service";
import { ContractDocumentView } from "@/components/ContractDocumentView";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { getOfferCompensationDefaults } from "@/data/contractor/offerCompensationDefaults";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MarketplaceService } from "@/services/marketplace.service";
import {
  extractImportPhotoStoragePath,
  getImportPhotoStableKey,
} from "@/data/import/importPhotoUrls";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { createSignedStorageFullImageUrl } from "@/lib/storageImageUrls";
import {
  getPreferredOfferRecipientId,
  resolveOfferWorkspaceState,
  shouldBlockOfferRecipientChange,
} from "@/pages/jobOfferReviewWorkspaceState";

const OFFER_TTL_PRESETS: Array<{ minutes: number; label: string }> = [
  { minutes: 60, label: "1h" },
  { minutes: 120, label: "2h" },
  { minutes: 240, label: "4h" },
  { minutes: 480, label: "8h" },
  { minutes: 1440, label: "24h" },
  { minutes: 2880, label: "48h" },
];
const INSTALLER_OFFERS_TIME_ZONE = "America/New_York";

const VIEWER_ROLES = new Set([
  "super_admin",
  "admin",
  "ops_manager",
  "head_of_installation",
  "it_staff",
]);

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-2">
      {children}
    </h2>
  );
}

function MetaCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium truncate">{value ?? "—"}</div>
    </div>
  );
}

function normalizeCompanyName(value?: string | null): string {
  return (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

function formatInstallerOffersDate(value?: string | null): string {
  if (!value) return "—";
  const trimmed = String(value).trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return `${Number(month)}/${Number(day)}/${year}`;
  }
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, { timeZone: INSTALLER_OFFERS_TIME_ZONE });
}

function formatInstallerOffersDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString(undefined, { timeZone: INSTALLER_OFFERS_TIME_ZONE });
}

function isRenderablePhotoUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  if (
    v.startsWith("http://") ||
    v.startsWith("https://") ||
    v.startsWith("data:") ||
    v.startsWith("blob:") ||
    v.startsWith("/")
  ) {
    return true;
  }
  return false;
}

function extractPhotoRef(photo: unknown): string {
  if (typeof photo === "string") return photo.trim();
  if (!photo || typeof photo !== "object") return "";
  const candidate = photo as Record<string, unknown>;
  const direct =
    candidate.url ??
    candidate.src ??
    candidate.preview ??
    candidate.previewUrl ??
    candidate.signedUrl ??
    candidate.filePath ??
    candidate.file_path ??
    candidate.storagePath ??
    candidate.path;
  return typeof direct === "string" ? direct.trim() : "";
}

function samePhotoList(
  a: Array<{ src: string; caption: string }>,
  b: Array<{ src: string; caption: string }>,
) {
  return (
    a.length === b.length &&
    a.every((value, index) => value.src === b[index]?.src && value.caption === b[index]?.caption)
  );
}

function TruncatedCell({ value, maxLen = 28 }: { value: string; maxLen?: number }) {
  const display = value.length > maxLen ? `${value.slice(0, maxLen)}…` : value;
  if (value.length <= maxLen) return <>{display}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="truncate block max-w-[200px] cursor-default">{display}</span>
      </TooltipTrigger>
      <TooltipContent side="top" align="start">
        <p className="font-mono text-xs max-w-xs break-words">{value}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export default function JobOfferReviewWorkspace() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { role } = useRole();
  const { jobs, marketplaceOffers, salesOpportunities, appointmentResults } = useAppData();
  const { contractors } = useReactQueryContractors();
  const { payProfiles } = useReactQueryPayProfiles();
  const onboarding = useOnboarding();
  const {
    create: createOffer,
    extend: extendOffer,
    changeWindow: changeOfferWindow,
  } = useMarketplaceOffers();
  const fin = useFinancialPermissions();
  const canViewInternalEconomics = fin.viewInternalCost && fin.viewMargin;

  // Prefer DB-backed job data; only fall back to in-memory app job when DB data
  // is unavailable (e.g. transient load state).
  const { job: dbJob, jobMaterials } = useJobDetailQuery(jobId ?? "");
  const appJob = useMemo(() => jobs.find((j) => j.id === jobId), [jobs, jobId]);
  const job = dbJob ?? appJob;

  const [selectedContractorId, setSelectedContractorId] = useState<string | null>(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [acceptedReassignmentMode, setAcceptedReassignmentMode] = useState(false);
  // Live draft Offer Compensation bubbled up from CompensationBuilder so the
  // ProjectedJobEconomicsPanel reflects unsaved edits in real time. Pure
  // preview state — never written through to the job.
  const [draftPayout, setDraftPayout] = useState<CompensationDraftPreview | null>(null);

  // Invoice, if any, for this job's payout — fetched once we know the job id;
  // getInvoiceForJob resolves to null (button stays hidden) when no payout/
  // invoice exists yet, so this is safe to run before an offer is accepted.
  const [invoiceItem, setInvoiceItem] = useState<ReadyToPayItem | null>(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);

  const loadInvoiceForJob = useCallback(async () => {
    if (!job?.id) return;
    const { data } = await InstallerPayoutsService.getInvoiceForJob(job.id);
    setInvoiceItem(data);
  }, [job?.id]);

  useEffect(() => {
    void loadInvoiceForJob();
  }, [loadInvoiceForJob]);

  // Hoist below memos above early returns — null-safe when job is undefined.
  const eligibility = useMemo(
    () =>
      job
        ? contractors.map((contractor) => {
            const parentContractor = contractor.parentEntityId
              ? contractors.find((candidate) => candidate.id === contractor.parentEntityId)
              : undefined;

            return resolveContractorEligibility(contractor, {
              payProfiles,
              job,
              complianceDocuments: onboarding.complianceDocuments,
              onboardingSessions: onboarding.sessions,
              parentContractor,
            });
          })
        : [],
    [job, contractors, payProfiles, onboarding.complianceDocuments, onboarding.sessions],
  );
  const eligibleList = useMemo(
    () => eligibility.filter((e) => e.eligible),
    [eligibility],
  );

  const assignedContractorId =
    job?.assignedSubcontractorId
    ?? (job as { contractorId?: string } | undefined)?.contractorId
    ?? null;
  const assignedContractorName = String(job?.assignedSubcontractor ?? "").trim();
  const assignedNameNorm = normalizeCompanyName(assignedContractorName);

  const decoratedEligible = useMemo(
    () =>
      eligibleList
        .map((e) => {
          const c = contractors.find((cc) => cc.id === e.contractorId);
          if (!c) return null;
          const av = getContractorAvailability(c, jobs);
          const isDispatchAssigned =
            (assignedContractorId && c.id === assignedContractorId) ||
            (assignedNameNorm.length > 0 && normalizeCompanyName(c.companyName) === assignedNameNorm);
          const bucket = bucketFor(av.state);
          return {
            entry: e,
            contractor: c,
            availability: av,
            bucket,
            effectiveBucket: isDispatchAssigned ? "available" : bucket,
            isDispatchAssigned: Boolean(isDispatchAssigned),
          };
        })
        .filter(
          (
            x,
          ): x is {
            entry: typeof eligibleList[number];
            contractor: typeof contractors[number];
            availability: ReturnType<typeof getContractorAvailability>;
            bucket: AvailabilityBucket;
            effectiveBucket: AvailabilityBucket;
            isDispatchAssigned: boolean;
          } => x !== null,
        )
        .sort((a, b) => {
          const rank: Record<AvailabilityBucket, number> = {
            available: 0,
            limited: 1,
            off: 2,
          };
          return rank[a.effectiveBucket] - rank[b.effectiveBucket];
        }),
    [eligibleList, contractors, jobs, assignedContractorId, assignedNameNorm],
  );

  const fleetDiagnostic = useMemo(() => {
    const totalContractors = contractors.length;
    const totalEligible = eligibleList.length;
    const reasonCounts: Record<string, { label: string; count: number }> = {};
    eligibility
      .filter((e) => !e.eligible)
      .forEach((e) => {
        e.factors
          .filter((f) => f.blocking && !f.passed)
          .forEach((f) => {
            if (!reasonCounts[f.key]) {
              reasonCounts[f.key] = { label: f.label, count: 0 };
            }
            reasonCounts[f.key].count += 1;
          });
      });
    const topReasons = Object.entries(reasonCounts)
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
    const contractorBreakdown = eligibility
      .filter((e) => !e.eligible)
      .map((e) => {
        const contractor = contractors.find((c) => c.id === e.contractorId);
        const reasons = e.factors
          .filter((f) => f.blocking && !f.passed)
          .map((f) => f.label);
        return {
          contractorId: e.contractorId,
          contractorName: contractor?.companyName ?? e.contractorId,
          reasons,
        };
      })
      .filter((entry) => entry.reasons.length > 0)
      .slice(0, 6);
    return { totalContractors, totalEligible, topReasons, contractorBreakdown };
  }, [contractors, eligibility, eligibleList]);

  const jobOffers = useMemo(
    () =>
      job
        ? marketplaceOffers
            .filter((o) => o.jobId === job.id)
            .sort(
              (a, b) =>
                new Date(b.sentAt || 0).getTime() - new Date(a.sentAt || 0).getTime(),
            )
        : [],
    [marketplaceOffers, job],
  );
  const {
    activeOffer,
    acceptedOffer,
    activePendingOffer,
    terminalLatestOffer,
    terminalReofferFlow,
  } = useMemo(
    () => resolveOfferWorkspaceState(jobOffers),
    [jobOffers],
  );
  const acceptedReassignmentFlow =
    acceptedReassignmentMode &&
    !!acceptedOffer &&
    !activePendingOffer &&
    !terminalReofferFlow;
  const dbBackedBom = useMemo(
    () =>
      (jobMaterials ?? [])
        .map((row) => {
          const materialRow = row as Record<string, unknown>;
          const sku = String(materialRow.sku ?? "").trim();
          const itemName = String(materialRow.item_name ?? "").trim();
          const quantityRequired = Number(materialRow.quantity_required ?? 0);
          if (!sku || !itemName || !Number.isFinite(quantityRequired) || quantityRequired <= 0) return null;
          return { sku, itemName, quantityRequired };
        })
        .filter((row): row is { sku: string; itemName: string; quantityRequired: number } => row !== null),
    [jobMaterials],
  );

  const rawPhotoRefs = useMemo(
    () =>
      (Array.isArray(job?.photos) ? job.photos : [])
        .map(extractPhotoRef)
        .filter(Boolean),
    [job],
  );
  const rawPhotoKey = useMemo(() => rawPhotoRefs.join("\n"), [rawPhotoRefs]);
  const [displayPhotos, setDisplayPhotos] = useState<Array<{ src: string; caption: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    const photoRefs = rawPhotoKey ? rawPhotoKey.split("\n").filter(Boolean) : [];
    const currentJobId = String(job?.id ?? "").trim();

    void Promise.all([
      photoRefs.length > 0
        ? Promise.all(
            photoRefs.map(async (photoRef) => {
              if (isRenderablePhotoUrl(photoRef)) return photoRef;
              const storagePath = extractImportPhotoStoragePath(photoRef);
              if (!storagePath) return "";
              try {
                return await createSignedStorageFullImageUrl(storagePath, {
                  bucket: "job-import-photos",
                  expiresIn: 60 * 60,
                });
              } catch (error) {
                console.warn("Failed to sign offer review import photo ref", photoRef, error);
                return "";
              }
            }),
          )
        : Promise.resolve([] as string[]),
      currentJobId
        ? getJobSitePhotoMeta(currentJobId).catch((error) => {
            console.warn("Failed to resolve offer review linked quote/site photos", error);
            return [] as Array<{ src: string; caption: string }>;
          })
        : Promise.resolve([] as Array<{ src: string; caption: string }>),
    ]).then(([importUrls, sitePhotos]) => {
      if (cancelled) return;
      const seen = new Set<string>();
      const merged = [
        ...sitePhotos,
        ...importUrls.map((src, index) => ({
          src,
          caption: `Job photo ${index + 1}`,
        })),
      ].filter((photo) => {
        const src = String(photo.src ?? "").trim();
        if (!src || seen.has(src)) return false;
        seen.add(src);
        return true;
      });
      setDisplayPhotos((current) => (samePhotoList(current, merged) ? current : merged));
    });

    return () => {
      cancelled = true;
    };
  }, [job?.id, rawPhotoKey]);

  if (!VIEWER_ROLES.has(role)) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <p className="text-sm text-muted-foreground">
          You don't have access to the Offer Review workspace.
        </p>
        <Button asChild variant="outline" size="sm" className="mt-4">
          <Link to="/installer-offers">Back to Installer Offers</Link>
        </Button>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <p className="text-sm text-muted-foreground">Job not found.</p>
        <Button asChild variant="outline" size="sm" className="mt-4">
          <Link to="/installer-offers">Back to Installer Offers</Link>
        </Button>
      </div>
    );
  }

  async function syncOfferToPortal(offer: {
    id: string;
    contractorId: string;
    payoutAmountSnapshot?: number;
    expiresAt: string;
  }) {
    const orgId = await getCurrentOrganizationId();

    const photoMeta = job.id
      ? await getJobSitePhotoMeta(String(job.id))
      : [];
    const signedPhotoUrls = photoMeta.map((p) => p.src);

    const briefSnapshot = getJobBriefing(job, undefined, signedPhotoUrls);
    const dbLineItems = (job.materialsList ?? []).map((item) => ({
      sku: item.sku,
      name: item.itemName,
      quantity: item.quantityRequired,
    }));
    const jobMetadata = {
      customerName: job.customerName,
      address: job.address,
      projectType: job.projectType,
      scheduledInstallDate: job.scheduledInstallDate,
      scheduledStartTime: job.scheduledStartTime,
      scheduledEndTime: job.scheduledEndTime,
      timeSlot: job.timeSlot,
      scopeSummary: job.scopeSummary,
      notes: job.notes,
      permitRequired: job.permitRequired,
      currentStage: job.currentStage,
      photos: photoMeta,
      measurements: (() => {
        const cfg = job.projectConfig;
        if (!cfg) return [];
        const lines: string[] = [];
        const wm = cfg.wallMeasurements;
        if (wm?.wallA?.width && wm?.wallA?.height)
          lines.push(`Wall A: ${wm.wallA.width}"W × ${wm.wallA.height}"H`);
        if (wm?.wallB?.width && wm?.wallB?.height)
          lines.push(`Wall B: ${wm.wallB.width}"W × ${wm.wallB.height}"H`);
        if (wm?.wallC?.width && wm?.wallC?.height)
          lines.push(`Wall C: ${wm.wallC.width}"W × ${wm.wallC.height}"H`);
        if (cfg.baseWidth && cfg.baseLength)
          lines.push(`Base: ${cfg.baseWidth}"W × ${cfg.baseLength}"L`);
        else if (cfg.bathroomBaseSize)
          lines.push(`Base size: ${cfg.bathroomBaseSize}`);
        else if (cfg.bathroomCustomSize)
          lines.push(`Base size: ${cfg.bathroomCustomSize}`);
        if (cfg.totalSqFt) lines.push(`Total sq ft: ${cfg.totalSqFt}`);
        if (cfg.measurementNotes) lines.push(cfg.measurementNotes);
        return lines;
      })(),
      materials: (job.materialsList ?? [])
        .map((item) => item.itemName)
        .filter(Boolean),
      offerCompensationConfig: job.offerCompensationConfig ?? null,
      site_constraints: briefSnapshot.riskFlags.map((f) => f.label),
      lineItems: dbLineItems,
      deliverables: job.boundJobSpec?.snapshot?.phases?.map((p: { label?: string }) => p.label).filter(Boolean) ?? [],
      exclusions: job.projectConfig
        ? Object.entries(job.projectConfig)
          .filter(([, v]) => v === false || v === "No")
          .map(([k]) => k)
        : [],
      materialsList: job.materialsList ?? null,
      financials: job.financials ?? null,
    };

    await supabase.functions.invoke("opportunity-publish", {
      body: {
        organizationId: orgId,
        appJobId: job.id,
        appOfferId: offer.id,
        contractorId: offer.contractorId,
        payoutAmountSnapshot: offer.payoutAmountSnapshot,
        projectType: (job.projectType ?? "other").toLowerCase(),
        city: job.address?.city,
        zip: job.address?.zip,
        scheduledDate: job.scheduledInstallDate ?? undefined,
        scopeSummary: job.scopeSummary ?? job.notes ?? undefined,
        expiresAt: offer.expiresAt,
        briefSnapshot,
        jobMetadata,
      },
    });
  }

  async function ensureOfferOrg(contractorId: string): Promise<boolean> {
    const orgId = await MarketplaceService.resolveOfferOrganizationId({
      jobId: job.id,
      contractorId,
    });
    if (orgId) return true;
    toast.error("Offer blocked", {
      description:
        "Cannot update offer because organization context is missing for this job/contractor.",
    });
    return false;
  }

  async function handleExtendOffer(offerId: string, contractorId: string, ttlMinutes: number) {
    if (!(await ensureOfferOrg(contractorId))) return;

    const result = extendOffer(offerId, ttlMinutes);
    if (!result.ok) {
      toast.error("Offer not updated", {
        description: "This offer can no longer be extended.",
      });
      return;
    }

    const ttlLabel =
      OFFER_TTL_PRESETS.find((preset) => preset.minutes === ttlMinutes)?.label ?? `${ttlMinutes}m`;
    toast.success("Offer extended", {
      description: `Offer expiry has been extended to ${ttlLabel}.`,
    });
    try {
      await syncOfferToPortal(result.offer);
    } catch (err) {
      console.warn("[JobOfferReviewWorkspace] opportunity-publish failed:", err);
      toast.error("Portal sync failed", {
        description: "The installer may not see the new expiry on the portal immediately.",
      });
    }
  }

  async function handleChangeOfferWindow(
    offerId: string,
    contractorId: string,
    ttlMinutes: number,
  ) {
    if (!(await ensureOfferOrg(contractorId))) return;

    const result = changeOfferWindow(offerId, ttlMinutes);
    if (!result.ok) {
      toast.error("Offer not updated", {
        description: "This offer window can no longer be changed.",
      });
      return;
    }

    const ttlLabel =
      OFFER_TTL_PRESETS.find((preset) => preset.minutes === ttlMinutes)?.label ?? `${ttlMinutes}m`;
    toast.success("Offer window changed", {
      description: `Offer window has been reset to ${ttlLabel} from the original send time.`,
    });
    try {
      await syncOfferToPortal(result.offer);
    } catch (err) {
      console.warn("[JobOfferReviewWorkspace] opportunity-publish failed:", err);
      toast.error("Portal sync failed", {
        description: "The installer may not see the updated offer window on the portal immediately.",
      });
    }
  }

  const scope = String(job.scopeSummary ?? "").trim();
  const notes = String(job.notes ?? "").trim();
  const showNotes = notes.length > 0 && notes !== scope;
  const hasScopeContent = scope.length > 0 || showNotes;

  const permitSignals = resolvePermitOperationalSignals(job);
  const comparables = getComparableJobOffers({
    job,
    jobs,
    offers: marketplaceOffers,
    opportunities: salesOpportunities,
    limit: 5,
  });

  const allowsZeroPayoutForOffer = job.offerCompensationConfig?.payoutOption === "revenue_share";
  const compensationDefaults = getOfferCompensationDefaults(job);
  const savedIncentiveTotal = (job.offerCompensationConfig?.incentives ?? []).reduce((sum, item) => {
    const amount = Number(item.amount ?? 0);
    return Number.isFinite(amount) && amount > 0 ? sum + Math.round(amount) : sum;
  }, 0);
  const savedPayoutOptionLabel = (() => {
    const config = job.offerCompensationConfig;
    if (!config) return "Manual";
    if (config.payoutOption === "revenue_share") {
      return config.revenueShareMode === "percentage"
        ? "Revenue Share · % of Net"
        : "Revenue Share · Fixed Amount";
    }
    if (config.sourceMode === "suggested") return "Suggested";
    if (config.sourceMode === "percent") return "Percent";
    return "Manual";
  })();

  // Prefer live draft payout -> saved payout -> default model payout.
  const effectiveOfferPayout =
    draftPayout?.amount !== null && draftPayout?.amount !== undefined
      ? draftPayout.amount
      : (typeof job.payoutAmount === "number" && (job.payoutAmount > 0 || (allowsZeroPayoutForOffer && job.payoutAmount === 0)))
        ? job.payoutAmount
        : (compensationDefaults.defaultAmount ?? 0);
  const payout = effectiveOfferPayout;
  const offerCompensationSummary = {
    basePayout: effectiveOfferPayout,
    incentiveTotal: draftPayout?.incentiveTotal ?? savedIncentiveTotal,
    effectiveOfferTotal:
      draftPayout?.effectiveOfferTotal
      ?? (effectiveOfferPayout + savedIncentiveTotal),
    payoutOptionLabel: draftPayout?.payoutOptionLabel ?? savedPayoutOptionLabel,
    contractValue: draftPayout?.contractValue ?? compensationDefaults.contractValue,
    financeFeePct: draftPayout?.financeFeePct ?? compensationDefaults.financeFeePct,
    financeFeeAmount: draftPayout?.financeFeeAmount ?? compensationDefaults.financeFeeAmount,
    netContract: draftPayout?.netContract ?? compensationDefaults.netContract,
    canViewContract:
      draftPayout?.canViewContract
      ?? (fin.viewContractValue && compensationDefaults.contractValue > 0),
  };

  const availableCount = decoratedEligible.filter((d) => d.effectiveBucket === "available").length;
  const limitedCount = decoratedEligible.filter((d) => d.effectiveBucket === "limited").length;
  const offCount = decoratedEligible.filter((d) => d.effectiveBucket === "off").length;
  const hasAlternativeEligibleInstaller =
    !!acceptedOffer &&
    decoratedEligible.some((entry) => entry.contractor.id !== acceptedOffer.contractorId);

  const selectedDecorated =
    decoratedEligible.find(
      (d) =>
        d.contractor.id ===
        getPreferredOfferRecipientId({
          selectedContractorId,
          terminalLatestOfferContractorId: terminalLatestOffer?.contractorId ?? null,
          acceptedOfferContractorId: acceptedOffer?.contractorId ?? null,
          assignedContractorId,
          assignedContractorName,
          eligibleContractors: decoratedEligible.map((d) => d.contractor),
          preferDifferentFromAccepted: acceptedReassignmentFlow,
        }),
    );
  const selectedContractorObj = selectedDecorated?.contractor;
  const selectedAvailability = selectedDecorated?.availability;
  const selectedBucket = selectedDecorated?.effectiveBucket ?? "available";
  const selectedMatchesAcceptedOffer =
    acceptedReassignmentFlow &&
    !!acceptedOffer &&
    selectedContractorObj?.id === acceptedOffer.contractorId;

  const assignmentMismatch = shouldBlockOfferRecipientChange({
    selectedContractor: selectedContractorObj,
    assignedContractorId,
    assignedContractorName,
    terminalReofferFlow,
    acceptedReassignmentFlow,
  });

  const needsOverride = selectedBucket !== "available";
  const overrideOk = !needsOverride || overrideReason.trim().length >= 10;

  const canSend =
    (payout > 0 || allowsZeroPayoutForOffer) &&
    decoratedEligible.length > 0 &&
    !!selectedContractorObj &&
    overrideOk &&
    !assignmentMismatch &&
    !selectedMatchesAcceptedOffer;

  useEffect(() => {
    if (!acceptedOffer || activePendingOffer || terminalReofferFlow) {
      setAcceptedReassignmentMode(false);
    }
  }, [acceptedOffer, activePendingOffer, terminalReofferFlow]);

  const warnings: { tone: "warn" | "bad"; text: string }[] = [];
  if (payout === 0 && !allowsZeroPayoutForOffer) {
    warnings.push({ tone: "bad", text: "Offer compensation not set." });
  }
  if (eligibleList.length === 0)
    warnings.push({ tone: "bad", text: "No eligible installers for this job." });
  if (permitSignals.operationalSeverity === "blocker")
    warnings.push({ tone: "bad", text: `Permit blocker: ${permitSignals.summaryLabel}` });
  else if (permitSignals.operationalSeverity === "warn")
    warnings.push({ tone: "warn", text: `Permit watch: ${permitSignals.summaryLabel}` });
  if (dbBackedBom.length === 0)
    warnings.push({ tone: "warn", text: "No DB-backed job materials found for this job." });

  const showOfferComposer = acceptedReassignmentFlow || !activeOffer;

  return (
    <>
      <CockpitPage
        eyebrow="Installer Offers"
        title="Offer Review"
        subtitle={`Review scope and set installer compensation for ${job.customerName ?? job.id} before sending an offer.`}
        maxWidth="7xl"
        controls={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/installer-offers")}>
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/jobs/${job.id}`}>
                Open Job <ExternalLink className="h-3.5 w-3.5 ml-1" />
              </Link>
            </Button>
          </div>
        }
      >
        {/* 1. Header strip */}
        <Card className="p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <MetaCell label="Customer" value={job.customerName} />
            <MetaCell
              label="Job ID"
              value={(job as { jobNumber?: string }).jobNumber ?? String(job.id).slice(0, 8).toUpperCase()}
            />
            <MetaCell label="Project Type" value={job.projectType} />
            <MetaCell label="Stage" value={job.currentStage} />
            <MetaCell
              label="Target Install"
              value={
                job.scheduledInstallDate || job.targetInstallDate
                  ? formatInstallerOffersDate(job.scheduledInstallDate || job.targetInstallDate)
                  : "—"
              }
            />
            <MetaCell
              label="Location"
              value={[job.address?.city, job.region].filter(Boolean).join(", ") || "—"}
            />
          </div>
          <div className="mt-3">
            <SectionHeading>Contract</SectionHeading>
            {job.contractDocument ? (
              <ContractDocumentView contract={job.contractDocument} compact />
            ) : (
              <p className="text-xs text-muted-foreground italic">
                No contract document on file for this job.
              </p>
            )}
          </div>
          {warnings.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {warnings.map((w, i) => (
                <Badge
                  key={i}
                  variant={w.tone === "bad" ? "destructive" : "secondary"}
                  className="text-[11px]"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {w.text}
                </Badge>
              ))}
            </div>
          )}
        </Card>

        {/* Two-column layout — left holds project context (Scope, Photos, BOM,
            Comparables); right holds the offer-decision stack (Compensation,
            Projected Economics, Send Offer). Each column stacks vertically so
            the left fills the dead space created by the taller right stack. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {/* ── Left column ── */}
          <div className="space-y-4">
            {/* 2. Scope & Notes */}
            <Card className="p-4">
              <SectionHeading>Scope</SectionHeading>
              {hasScopeContent ? (
                <div className="space-y-3">
                  {scope && (
                    <div className="rounded-md border bg-muted/20 p-3">
                      <div className="mb-2 flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary/40" />
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                          Scope Summary
                        </div>
                      </div>
                      <p className="whitespace-pre-line text-sm leading-6 text-foreground">{scope}</p>
                    </div>
                  )}
                  {showNotes && (
                    <div className="rounded-md border bg-muted/20 p-3">
                      <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        Description / Notes
                      </div>
                      <p className="whitespace-pre-line text-sm leading-6 text-muted-foreground">{notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No scope summary recorded.</p>
              )}
            </Card>

            {/* 3. Photos */}
            <Card className="p-4">
              <SectionHeading>Photos ({displayPhotos.length})</SectionHeading>
              {displayPhotos.length > 0 ? (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {displayPhotos.map((photo, i) => (
                    <a
                      key={getImportPhotoStableKey(photo.src, `offer-photo-${i}`)}
                      href={photo.src}
                      target="_blank"
                      rel="noreferrer"
                      className="aspect-square rounded-md overflow-hidden border bg-muted block"
                      title={photo.caption}
                    >
                      <img
                        src={photo.src}
                        alt={photo.caption || `Job photo ${i + 1}`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-muted-foreground italic">
                  <ImageIcon className="h-3.5 w-3.5" />
                  No photos uploaded.
                </div>
              )}
            </Card>

            {/* 4. BOM */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <SectionHeading>Bill of Materials</SectionHeading>
                <Badge variant="outline" className="text-[10px]">
                  DB-backed (job_materials)
                </Badge>
              </div>
              {dbBackedBom.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground">
                      <tr className="border-b">
                        <th className="text-left py-1.5 font-medium">SKU</th>
                        <th className="text-left py-1.5 font-medium">Item</th>
                        <th className="text-right py-1.5 font-medium">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbBackedBom.map((it, i) => (
                        <tr key={`${it.sku}-${i}`} className="border-b border-border/40">
                          <td className="py-1.5 font-mono text-[11px]">
                            <TruncatedCell value={it.sku} maxLen={20} />
                          </td>
                          <td className="py-1.5">
                            <TruncatedCell value={it.itemName} maxLen={30} />
                          </td>
                          <td className="py-1.5 text-right tabular-nums">{it.quantityRequired}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No DB-backed materials found in `job_materials` for this job.
                </p>
              )}
            </Card>

            {/* 6. Comparables — accepted-offer snapshots only, sample ≥ 3 to show stats */}
            <Card className="p-4">
              <SectionHeading>Comparable Accepted Offers ({comparables.count})</SectionHeading>
              {comparables.hasDisplayableSample ? (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-3 pb-3 border-b">
                    <MetaCell
                      label="Median Accepted"
                      value={`$${comparables.median!.toLocaleString()}`}
                    />
                    <MetaCell label="Min" value={`$${comparables.min!.toLocaleString()}`} />
                    <MetaCell label="Max" value={`$${comparables.max!.toLocaleString()}`} />
                  </div>
                  <ul className="space-y-1.5">
                    {comparables.matches.map((c) => (
                      <li
                        key={c.job.id}
                        className="flex items-center justify-between text-xs gap-2"
                      >
                        <Link
                          to={`/jobs/${c.job.id}`}
                          className="truncate hover:underline"
                        >
                          {c.job.customerName ?? c.job.id} ·{" "}
                          <span className="text-muted-foreground">{c.city ?? "—"}</span>
                        </Link>
                        <span className="tabular-nums shrink-0">
                          ${c.acceptedPayoutSnapshot.toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[10px] text-muted-foreground italic">
                    Based on accepted installer payouts. Cancelled, rescission, and finance-declined jobs are excluded.
                  </p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  Not enough accepted comparable jobs yet. Need{" "}
                  {Math.max(comparables.minSample - comparables.count, 0)} more before a
                  comparable band can be shown.
                </p>
              )}
            </Card>
          </div>

          {/* ── Right column — offer decision stack ── */}
          <div className="space-y-4">
            <Card className="p-4">
              <CompensationBuilder
                job={job}
                comparables={comparables}
                onDraftChange={setDraftPayout}
                acceptedOffer={acceptedOffer
                  ? (() => {
                      const c = contractors.find((cc) => cc.id === acceptedOffer.contractorId);
                      return {
                        contractorName: c?.companyName ?? acceptedOffer.contractorId,
                        acceptedAt: acceptedOffer.respondedAt ?? acceptedOffer.sentAt,
                      };
                    })()
                  : null}
              />
            </Card>

            <ProjectedJobEconomicsPanel
              job={job}
              visible={canViewInternalEconomics}
              draftPayoutAmount={draftPayout?.amount ?? null}
            />

            {activeOffer && !acceptedReassignmentFlow ? (
              <Card className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <SectionHeading>Offer Status</SectionHeading>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      activeOffer.status === "accepted"
                        ? "border-green-500/40 text-green-600"
                        : "border-primary/40 text-primary"
                    }`}
                  >
                    {activeOffer.status === "accepted"
                      ? "Accepted"
                      : "Offered"}
                  </Badge>
                </div>
                <div className="rounded-md border bg-muted/20 p-3 text-xs space-y-2">
                  {(() => {
                    const offerContractor = contractors.find((c) => c.id === activeOffer.contractorId);
                    const offeredToName = offerContractor?.companyName ?? activeOffer.contractorId;
                    const sentDate = formatInstallerOffersDate(activeOffer.sentAt);
                    const acceptedDate = activeOffer.respondedAt
                      ? formatInstallerOffersDate(activeOffer.respondedAt)
                      : activeOffer.status === "accepted" && activeOffer.sentAt
                        ? formatInstallerOffersDate(activeOffer.sentAt)
                        : "—";
                    return (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Who was offered</span>
                          <span className="font-medium">{offeredToName}</span>
                        </div>
                        {activeOffer.status === "accepted" ? (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Who accepted</span>
                              <span className="font-medium">{offeredToName}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Accepted on</span>
                              <span className="tabular-nums">{acceptedDate}</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Sent on</span>
                            <span className="tabular-nums">{sentDate}</span>
                          </div>
                        )}
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Expires on</span>
                            <span className="tabular-nums">
                            {formatInstallerOffersDateTime(activeOffer.expiresAt)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Sent by</span>
                            <span className="tabular-nums">{activeOffer.sentBy}</span>
                          </div>
                      </>
                    );
                  })()}
                </div>
                {activeOffer.status === "accepted" && invoiceItem?.invoice && (
                  <div className="mt-3">
                    <Button size="sm" variant="outline" onClick={() => setInvoiceDialogOpen(true)}>
                      View Invoice
                    </Button>
                  </div>
                )}
                {activeOffer.status !== "accepted" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm">
                          Extend by
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-36">
                        {OFFER_TTL_PRESETS.map((preset) => (
                          <DropdownMenuItem
                            key={`extend-${preset.minutes}`}
                            onClick={() => {
                              void handleExtendOffer(
                                activeOffer.id,
                                activeOffer.contractorId,
                                preset.minutes,
                              );
                            }}
                          >
                            {preset.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline">
                          Change window
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-36">
                        {OFFER_TTL_PRESETS.map((preset) => (
                          <DropdownMenuItem
                            key={`change-window-${preset.minutes}`}
                            onClick={() => {
                              void handleChangeOfferWindow(
                                activeOffer.id,
                                activeOffer.contractorId,
                                preset.minutes,
                              );
                            }}
                          >
                            {preset.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                {activeOffer.status === "accepted" && !activePendingOffer && (
                  <div className="mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!hasAlternativeEligibleInstaller}
                      onClick={() => {
                        setAcceptedReassignmentMode(true);
                        setOverrideReason("");
                        const preferredReplacement = decoratedEligible.find(
                          (entry) => entry.contractor.id !== activeOffer.contractorId,
                        );
                        setSelectedContractorId(
                          preferredReplacement?.contractor.id ?? activeOffer.contractorId,
                        );
                      }}
                    >
                      Reassign to another installer
                    </Button>
                    {!hasAlternativeEligibleInstaller && (
                      <p className="mt-2 text-[10px] text-muted-foreground">
                        No other eligible installers are currently available for a reassignment offer.
                      </p>
                    )}
                  </div>
                )}
                <p className="mt-2 text-[10px] text-muted-foreground italic">
                  {activeOffer.status === "accepted"
                    ? "This offer has been accepted. Use reassignment only when you need to send a replacement offer without changing the current assignment until the new installer accepts."
                    : "This offer has been sent and is waiting for installer response. Extend adds time onto the current expiry, while Change window resets the total window from the original send time."}
                </p>
              </Card>
            ) : showOfferComposer ? (
              <Card className="p-4">
                <SectionHeading>
                  {terminalReofferFlow || acceptedReassignmentFlow ? "Send New Offer" : "Send Offer"}
                </SectionHeading>
                {acceptedReassignmentFlow && acceptedOffer && (
                  <div className="mb-3 rounded-md border bg-muted/20 p-3 text-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Current assigned installer</span>
                      <span className="font-medium">
                        {contractors.find((contractor) => contractor.id === acceptedOffer.contractorId)?.companyName
                          ?? acceptedOffer.contractorId}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Send a replacement offer to another installer without changing the current assignment. The job stays assigned to the accepted installer unless the new offer is accepted.
                    </p>
                  </div>
                )}
                {terminalLatestOffer && (
                  <div className="mb-3 rounded-md border bg-muted/20 p-3 text-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Latest offer result</span>
                      <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700">
                        {terminalLatestOffer.status === "declined" ? "Declined" : "Expired"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Previous installer</span>
                      <span className="font-medium">
                        {contractors.find((contractor) => contractor.id === terminalLatestOffer.contractorId)?.companyName
                          ?? terminalLatestOffer.contractorId}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Select the previous installer for a normal re-offer, or pick a different installer to send a new pending offer. Assignment will still wait for acceptance.
                    </p>
                  </div>
                )}

                {/* Availability breakdown — never empty when eligible exist */}
                <div className="flex flex-wrap items-center gap-1.5 mb-3 text-[11px]">
                  {decoratedEligible.length === 0 ? (
                    <Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive">
                      0 eligible of {fleetDiagnostic.totalContractors} contractors
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${availableCount === 0 ? "border-warning/40 text-warning" : ""}`}
                    >
                      {availableCount} available
                    </Badge>
                  )}
                  {limitedCount > 0 && (
                    <Badge variant="outline" className="text-[10px] border-warning/40 text-warning">
                      {limitedCount} limited / at capacity
                    </Badge>
                  )}
                  {offCount > 0 && (
                    <Badge variant="outline" className="text-[10px] border-muted-foreground/30 text-muted-foreground">
                      {offCount} PTO / paused
                    </Badge>
                  )}
                </div>

                {/* Visible available installer list — confirms who the offer
                    will be pushed to on the installer website portal. */}
                {availableCount > 0 && (
                  <div className="mb-3 rounded-md border bg-muted/30 p-2">
                    <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-1.5">
                      Available installers · pushed to installer portal
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {decoratedEligible
                        .filter((d) => d.effectiveBucket === "available")
                        .map((d) => (
                          <button
                            key={d.contractor.id}
                            type="button"
                            onClick={() => {
                              setSelectedContractorId(d.contractor.id);
                              setOverrideReason("");
                            }}
                            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                              selectedContractorObj?.id === d.contractor.id
                                ? "bg-primary/15 text-primary ring-1 ring-primary/30 border-primary/30"
                                : "bg-background hover:bg-muted border-border"
                            }`}
                          >
                            {d.contractor.companyName}
                            {d.isDispatchAssigned ? " · assigned" : ""}
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                {decoratedEligible.length > 0 ? (
                  <>
                    <Select
                      value={selectedContractorObj?.id}
                      onValueChange={(v) => {
                        setSelectedContractorId(v);
                        setOverrideReason("");
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Choose installer" />
                      </SelectTrigger>
                      <SelectContent>
                        {(["available", "limited", "off"] as AvailabilityBucket[]).map((b) => {
                          const rows = decoratedEligible.filter((d) => d.effectiveBucket === b);
                          if (rows.length === 0) return null;
                          return (
                            <SelectGroup key={b}>
                              <SelectLabel className="text-[10px] uppercase tracking-wider">
                                {AVAILABILITY_BUCKET_LABEL[b]}
                              </SelectLabel>
                              {rows.map((d) => (
                                <SelectItem key={d.contractor.id} value={d.contractor.id}>
                                  {d.contractor.companyName}
                                  <span className="ml-2 text-muted-foreground text-[10px]">
                                    · {d.isDispatchAssigned ? "assigned in dispatch" : describeAvailability(d.availability.state)}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          );
                        })}
                      </SelectContent>
                    </Select>

                    {selectedAvailability && needsOverride && (
                      <div className="mt-3 p-2.5 rounded-md border border-warning/40 bg-warning/5">
                        <p className="text-[11px] font-semibold text-warning flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {describeAvailability(selectedAvailability.state)}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Explain why this offer should go out anyway (≥10 chars).
                        </p>
                        <Textarea
                          value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          placeholder="e.g. customer specifically requested this installer"
                          className="mt-1.5 text-xs min-h-[60px]"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-md border border-warning/40 bg-warning/5 p-3">
                    <p className="text-xs font-semibold text-warning flex items-center gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      No installers available to offer this job
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {fleetDiagnostic.totalEligible === 0
                        ? `0 of ${fleetDiagnostic.totalContractors} contractors in your network qualify for this job.`
                        : `${fleetDiagnostic.totalEligible} eligible, but none are currently available for this time window.`}
                    </p>
                    {fleetDiagnostic.topReasons.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                          Why they're filtered out
                        </div>
                        <ul className="text-[11px] space-y-0.5">
                          {fleetDiagnostic.topReasons.map((r) => (
                            <li key={r.key} className="flex items-center justify-between gap-2">
                              <span className="text-muted-foreground">{r.label}</span>
                              <span className="tabular-nums">{r.count}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {fleetDiagnostic.contractorBreakdown.length > 0 && (
                      <div className="mt-2 space-y-1.5 rounded border border-warning/20 bg-background/40 p-2">
                        <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                          Contractor blockers
                        </div>
                        {fleetDiagnostic.contractorBreakdown.map((entry) => (
                          <div key={entry.contractorId} className="text-[11px]">
                            <div className="font-medium">{entry.contractorName}</div>
                            <div className="text-muted-foreground">
                              {entry.reasons.join(" · ")}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-2.5 pt-2.5 border-t border-warning/20 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                        Next steps
                      </div>
                      <ul className="text-[11px] text-muted-foreground space-y-0.5 list-disc pl-4">
                        <li>
                          Recruit new installers
                          {job.address?.city ? ` in ${job.address.city}` : " in this area"}.
                        </li>
                        <li>Adjust contractor coverage or marketplace opt-in.</li>
                        <li>Reschedule the install to a window with availability.</li>
                      </ul>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <Button asChild variant="outline" size="sm" className="h-7 text-[11px]">
                          <Link to="/contractors">Open Contractor Network</Link>
                        </Button>
                        <Button asChild variant="outline" size="sm" className="h-7 text-[11px]">
                          <Link to={`/jobs/${job.id}`}>Smart Dispatch Triage</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  className="w-full mt-3"
                  size="sm"
                  disabled={!canSend}
                  onClick={() => setOfferOpen(true)}
                >
                  {needsOverride
                    ? terminalReofferFlow || acceptedReassignmentFlow
                      ? "Send New Offer Anyway"
                      : "Send Offer Anyway"
                    : terminalReofferFlow || acceptedReassignmentFlow
                      ? "Send New Offer"
                      : "Send Offer"}
                </Button>
                {selectedMatchesAcceptedOffer && acceptedOffer && (
                  <p className="mt-2 text-[10px] text-warning">
                    Select a different installer to start a reassignment cycle. The current accepted installer is already assigned to this job.
                  </p>
                )}
                {assignmentMismatch && (
                  <p className="mt-2 text-[10px] text-warning">
                    Job is currently assigned to{" "}
                    {assignedContractorName || "another contractor"}. Reassign/unassign in Dispatch before sending this offer.
                  </p>
                )}
                {(terminalReofferFlow || acceptedReassignmentFlow) && !assignmentMismatch && selectedContractorObj && !selectedMatchesAcceptedOffer && (
                  <p className="mt-2 text-[10px] text-muted-foreground">
                    This will create a new pending offer for {selectedContractorObj.companyName}. The job assignment will not change unless that installer accepts.
                  </p>
                )}
                {acceptedReassignmentFlow && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setAcceptedReassignmentMode(false);
                        setOverrideReason("");
                        setSelectedContractorId(acceptedOffer?.contractorId ?? null);
                      }}
                    >
                      Cancel reassignment
                    </Button>
                  </div>
                )}

                <p className="mt-2 text-[10px] text-muted-foreground">
                  Customer contract is separate. This sends an installer-only offer.
                </p>
              </Card>
            ) : null}
          </div>
        </div>
      </CockpitPage>

      {selectedContractorObj && (
        <OfferContractorDialog
          open={offerOpen}
          onOpenChange={setOfferOpen}
          job={job}
          contractor={selectedContractorObj}
          baselineOverride={effectiveOfferPayout}
          compensationSummary={offerCompensationSummary}
          contractExecutedAt={(() => {
            if (!job.appointmentResultId) return undefined;
            const ar = appointmentResults.find((r) => r.id === job.appointmentResultId);
            return ar?.appointmentDatetime;
          })()}
          onConfirm={async (data) => {
            if (assignmentMismatch) {
              toast.error("Offer blocked", {
                description:
                  `This job is assigned to ${assignedContractorName || "another contractor"}. Reassign or unassign it before sending an offer to ${selectedContractorObj.companyName}.`,
              });
              return;
            }

            const orgId = await MarketplaceService.resolveOfferOrganizationId({
              jobId: job.id,
              contractorId: selectedContractorObj.id,
            });
            if (!orgId) {
              toast.error("Offer blocked", {
                description:
                  "Cannot send offer because organization context is missing for this job/contractor.",
              });
              return;
            }
            const selectedParentContractor = selectedContractorObj.parentEntityId
              ? contractors.find((c) => c.id === selectedContractorObj.parentEntityId)
              : undefined;
            const result = createOffer({
              job,
              contractor: selectedContractorObj,
              payProfiles,
              sentBy: role,
              payoutAmountOverride: effectiveOfferPayout,
              ttlMinutes: data.ttlMinutes,
              complianceDocuments: onboarding.complianceDocuments,
              onboardingSessions: onboarding.sessions,
              parentContractor: selectedParentContractor,
              allowAcceptedReassignment: acceptedReassignmentFlow,
            });
            if (!result.ok) {
              const failure = result as import("@/data/contractor/offerEngine").OfferEngineFailure;
              const reason =
                failure.reason === "ineligible"
                  ? "Contractor is not eligible for this job."
                  : failure.reason === "duplicate_pending"
                    ? "A pending offer already exists for this installer/job."
                    : failure.reason === "already_assigned"
                      ? "This job already has an accepted offer."
                      : failure.reason === "not_pending"
                        ? "Offer is no longer pending."
                        : "Offer could not be created.";
              toast.error("Offer not sent", { description: reason });
              return;
            }

            toast.success("Offer sent", {
              description: `Offer was created and sent to ${selectedContractorObj.companyName}.`,
            });
            setOfferOpen(false);
            const offer = (result as Extract<typeof result, { ok: true }>).offer;
            navigate("/installer-offers");

            // Publish to installer_opportunities so the portal can see the offer.
            void (async () => {
              try {
                await syncOfferToPortal(offer);
              } catch (err) {
                console.warn("[JobOfferReviewWorkspace] opportunity-publish failed:", err);
                toast.error("Portal sync failed", {
                  description: "The installer may not see this offer on the portal immediately.",
                });
              }
            })();
          }}
        />
      )}

      {invoiceItem?.invoice && (
        <InstallerInvoiceDialog
          open={invoiceDialogOpen}
          onOpenChange={setInvoiceDialogOpen}
          payout={invoiceItem.payout}
          invoice={invoiceItem.invoice}
          onVoided={() => void loadInvoiceForJob()}
        />
      )}
    </>
  );
}
